export const EMP_CARD_RE = /^EMP:[A-Za-z0-9]+:v\d+$/i;
export const OFFCUT_RE = /^(LT-|OCSF)/i;
export const LOC_RE = /^LOC:/i;
export const EQ_RE = /^EQ-/i;
// 原材料 BID 按 ADR-0002 编码 {TT}{CC}{NNNNNN} 10 位:
// RM 类型 + 2 字母公司代码 (SF=盛源锻造, SY=...) + 6 位流水
// 例 RMSF000001 / RMSY000034。MR- 是物料需求单老格式,保留兼容。
export const RAW_MATERIAL_RE = /^(RM[A-Z]{2}\d{6}$|MR-)/i;
export const PRE_INBOUND_RE = /^(preinbound:\d+|PIB:\d+)$/i;
// Backends may print the task entry as either the canonical DNT id or the
// delivery-note number carried by the scanner's qrText field.
export const DELIVERY_TASK_ID_RE = /^DNT:\d+$/i;
export const DELIVERY_NOTE_NO_RE = /^DN(?:-?\d+)+$/i;
export const DELIVERY_TASK_RE = /^(?:DNT:\d+|DN(?:-?\d+)+)$/i;
export const DELIVERY_LINE_RE = /^DNL:\d+$/i;
export const FORMAL_TRACE_RE = /^\d{8}$/;
export const PMIG_RE = /^PMIG-[A-Z0-9-]+$/i;

export type ScanCodeKind =
  | 'employee'
  | 'offcut'
  | 'location'
  | 'equipment'
  | 'rawMaterial'
  | 'preInboundOrder'
  | 'unknown'
  | 'empty';

export function extractPreInboundId(code: string): string {
  const match = code.match(/^(?:preinbound|PIB):(\d+)$/i);
  return match?.[1] || '';
}

interface ScannerJsonCode {
  parsed: boolean;
  source: 'none' | 'qrText' | 'result';
  text: string;
}

function readScannerText(value: unknown) {
  if (typeof value === 'string') {
    const text = value.trim();
    return text.toLowerCase() === 'null' ? '' : text;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return '';
}

function extractScannerJsonCode(value: string): ScannerJsonCode {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return { parsed: false, source: 'none', text: '' };
  }

  try {
    const payload = JSON.parse(trimmed) as { qrText?: unknown; result?: unknown };
    const result = readScannerText(payload.result);
    if (result) {
      return { parsed: true, source: 'result', text: result };
    }

    const qrText = readScannerText(payload.qrText);
    return {
      parsed: true,
      source: qrText ? 'qrText' : 'none',
      text: qrText,
    };
  } catch {
    return { parsed: false, source: 'none', text: '' };
  }
}

export function normalizeScannedCode(value: string) {
  const scannerJsonCode = extractScannerJsonCode(value);
  let code = scannerJsonCode.parsed ? scannerJsonCode.text : value.trim();

  code = code.replace(/[\u200B-\u200D\uFEFF]/g, '');
  code = code.replace(/\s+/g, '');
  code = code.replace(/：/g, ':');
  code = code.replace(/^\][A-Za-z0-9][0-9]/, '');

  if (scannerJsonCode.source !== 'result') {
    const extracted = code.match(
      /(EMP:[A-Za-z0-9]+:v\d+|PMI_TASK:\d+|PMI[A-Z]?-\d{8}-\d+|PMI\d{8}-\d+|preinbound:\d+|PIB:\d+|LOC(?:AL)?:[A-Za-z0-9-]+|LT-[A-Za-z0-9-]+|OCSF[A-Za-z0-9-]+|EQ-[A-Za-z0-9-]+|RM[A-Z]{2}\d{6}(?!\d)|MR-[A-Za-z0-9-]+)/i,
    );

    if (extracted?.[0]) {
      code = extracted[0];
    }
  }

  if (/^emp:/i.test(code)) {
    return code.replace(/^emp:/i, 'EMP:');
  }
  if (/^loc:/i.test(code)) {
    return code.replace(/^loc:/i, 'LOC:');
  }
  if (/^local:/i.test(code)) {
    return code.replace(/^local:/i, 'LOC:');
  }
  if (/^lt-/i.test(code)) {
    return code.replace(/^lt-/i, 'LT-');
  }
  if (/^ocsf/i.test(code)) {
    return code.replace(/^ocsf/i, 'OCSF');
  }
  if (/^eq-/i.test(code)) {
    return code.replace(/^eq-/i, 'EQ-');
  }
  if (/^pmi_task:/i.test(code)) {
    return code.replace(/^pmi_task:/i, 'PMI_TASK:');
  }
  if (/^pmi/i.test(code)) {
    return code.toUpperCase();
  }
  if (/^rm[a-z]{2}\d/i.test(code)) {
    return code.toUpperCase();
  }
  if (/^mr-/i.test(code)) {
    return code.replace(/^mr-/i, 'MR-');
  }
  if (/^preinbound:/i.test(code)) {
    return code.replace(/^preinbound:/i, 'preinbound:');
  }
  if (/^pib:/i.test(code)) {
    return code.replace(/^pib:/i, 'PIB:');
  }

  return code;
}

export type DeliveryBusinessCodePreference = 'tagged' | 'formal';

export function extractDeliveryBusinessCode(
  value: string,
  preference: DeliveryBusinessCodePreference = 'tagged',
) {
  const trimmed = value.trim();
  let candidates = [trimmed];

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const payload = JSON.parse(trimmed) as { qrText?: unknown; result?: unknown };
      // A task QR can be present in qrText while the scanner SDK also returns
      // an unrelated result value. Keep both fields and resolve by business
      // code kind instead of discarding qrText up front.
      candidates = [readScannerText(payload.result), readScannerText(payload.qrText)]
        .filter(Boolean);
    } catch {
      candidates = [trimmed];
    }
  }

  const decodedCandidates = candidates.map((candidate) => {
    const normalized = normalizeScannedCode(candidate);
    try {
      return decodeURIComponent(normalized);
    } catch {
      return normalized;
    }
  });

  const findTagged = () => {
    for (const candidate of decodedCandidates) {
      const tagged = candidate.match(/(?:DNT:\d+|DNL:\d+|DN(?:-?\d+)+|PMIG-[A-Z0-9-]+)/i)?.[0];
      if (tagged) {
        return tagged.toUpperCase();
      }
    }
    return '';
  };

  const findFormalTrace = () => {
    for (const candidate of decodedCandidates) {
      const formalTrace = candidate.match(/(?:^|\D)(\d{8})(?!\d)/)?.[1];
      if (formalTrace) {
        return formalTrace;
      }
    }
    return '';
  };

  return preference === 'formal'
    ? findFormalTrace() || findTagged()
    : findTagged() || findFormalTrace();
}

export function detectScanCodeKind(value: string): ScanCodeKind {
  const code = normalizeScannedCode(value);

  if (!code) {
    return 'empty';
  }
  if (EMP_CARD_RE.test(code)) {
    return 'employee';
  }
  if (OFFCUT_RE.test(code)) {
    return 'offcut';
  }
  if (LOC_RE.test(code)) {
    return 'location';
  }
  if (EQ_RE.test(code)) {
    return 'equipment';
  }
  if (PRE_INBOUND_RE.test(code)) {
    return 'preInboundOrder';
  }
  if (RAW_MATERIAL_RE.test(code)) {
    return 'rawMaterial';
  }
  return 'unknown';
}
